import { useEffect, useRef, useState } from "react";
import type { Client } from "@board/shared";
import { api } from "./api";
import { shrinkImage } from "./shrinkImage";
import { useBoard } from "./store";

/**
 * Editing a client board: what it is called, where the relationship stands, the
 * Slack channel it reports into, and the logo at the top of the column.
 *
 * Admin-only, and the database says so — migration 010 made every write to a
 * client admin-only. This form is not the boundary; it is what makes the
 * boundary legible, which is why it is simply not offered to a member.
 *
 * Nothing is written until Save. The logo is the one field that needs a request
 * of its own — bytes never travel through the generic PATCH — so it is held as
 * an intention here and applied in the same breath as the rest.
 */

/** Offered, not enforced. The column is free text: the database checks length, nothing else. */
const SUGGESTED_STATUSES = ["Active", "Waiting", "On hold", "Complete"];

const NAME_COLORS = [
  { value: "#1c3f8f", label: "Blue" },
  { value: "#16181a", label: "Black" },
  { value: "#b52b2f", label: "Red" },
  { value: "#1d7047", label: "Green" },
  { value: "#6e3ca0", label: "Purple" },
  { value: "#b05d16", label: "Orange" },
] as const;

type LogoChange = { kind: "keep" } | { kind: "replace"; data: string } | { kind: "remove" };

/** Same members, same order, either way the list is read. */
function sameSet(a: Set<string>, b: Set<string>): boolean {
  return a.size === b.size && [...a].every((id) => b.has(id));
}

export function ClientEditor({ client, onDone }: { client: Client; onDone: () => void }) {
  const updateClient = useBoard((s) => s.updateClient);
  const setClientLogo = useBoard((s) => s.setClientLogo);
  const clearClientLogo = useBoard((s) => s.clearClientLogo);
  const deleteClient = useBoard((s) => s.deleteClient);
  const users = useBoard((s) => s.users);

  const [name, setName] = useState(client.name);
  const [nameColor, setNameColor] = useState(client.name_color);
  const [status, setStatus] = useState(client.status ?? "");
  const [channel, setChannel] = useState(client.slack_channel_id ?? "");
  const [logo, setLogo] = useState<LogoChange>({ kind: "keep" });
  const [isPrivate, setIsPrivate] = useState(client.is_private);
  // Who is on the list now, and who was on it when the form opened — so Save
  // can write the list only when it actually changed.
  const [allowed, setAllowed] = useState<Set<string>>(new Set());
  const loaded = useRef<Set<string>>(new Set());
  const [accessState, setAccessState] = useState<"loading" | "ready" | "error">("loading");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const file = useRef<HTMLInputElement>(null);

  // Fetched rather than carried in the board snapshot: it is admin-only, and a
  // list of who can see a private board has no business in every member's first
  // paint.
  useEffect(() => {
    let live = true;
    api.clients
      .access(client.id)
      .then((result) => {
        if (!live) return;
        loaded.current = new Set(result.user_ids);
        setAllowed(new Set(result.user_ids));
        setAccessState("ready");
      })
      .catch(() => {
        if (live) setAccessState("error");
      });
    return () => {
      live = false;
    };
  }, [client.id]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onDone();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onDone]);

  const current = client.logo_updated_at
    ? `/api/clients/${client.id}/logo?v=${encodeURIComponent(client.logo_updated_at)}`
    : null;
  const preview = logo.kind === "replace" ? logo.data : logo.kind === "remove" ? null : current;

  async function pick(chosen: File | null) {
    setProblem(null);
    if (!chosen) return;
    try {
      setLogo({ kind: "replace", data: await shrinkImage(chosen) });
    } catch {
      setProblem("Couldn’t read that image.");
    }
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    const trimmed = name.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    setProblem(null);
    try {
      // The fields first. If the logo then fails, the rest of the edit is
      // already saved and only the picture needs doing again.
      await updateClient(client.id, {
        name: trimmed,
        name_color: nameColor,
        status: status.trim() || null,
        slack_channel_id: channel.trim() || null,
        is_private: isPrivate,
      });
      if (!sameSet(allowed, loaded.current)) {
        await api.clients.setAccess(client.id, [...allowed]);
        loaded.current = new Set(allowed);
      }
      if (logo.kind === "replace") await setClientLogo(client.id, logo.data);
      if (logo.kind === "remove" && client.logo_updated_at) await clearClientLogo(client.id);
      onDone();
    } catch {
      setProblem("That didn’t save. Only an admin can change a client.");
    } finally {
      setBusy(false);
    }
  }

  async function moveToRecycleBin() {
    if (busy || !window.confirm(`Move “${client.name}” and everything on its board to the recycle bin?`)) return;
    setBusy(true);
    setProblem(null);
    try {
      await deleteClient(client.id);
      onDone();
    } catch {
      setProblem("That board couldn’t be moved to the recycle bin.");
      setBusy(false);
    }
  }

  return (
    <div className="sheet__scrim" onPointerDown={onDone}>
      <form
        className="cedit"
        onSubmit={submit}
        onPointerDown={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`Edit ${client.name}`}
      >
        <header className="cedit__top">
          <h2>Client board</h2>
          <button type="button" className="sheet__close" onClick={onDone} aria-label="Close">
            ×
          </button>
        </header>

        <div className="cedit__body">
          <div className="cedit__logo">
            {preview ? <img src={preview} alt="" /> : <span>No logo</span>}
            <div className="cedit__logoactions">
              <button type="button" onClick={() => file.current?.click()}>
                {preview ? "Replace…" : "Add a logo…"}
              </button>
              {preview ? (
                <button type="button" className="cedit__remove" onClick={() => setLogo({ kind: "remove" })}>
                  Remove
                </button>
              ) : null}
              {logo.kind !== "keep" ? (
                <button type="button" onClick={() => setLogo({ kind: "keep" })}>
                  Undo
                </button>
              ) : null}
            </div>
            <input
              ref={file}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              hidden
              onChange={(event) => {
                void pick(event.target.files?.[0] ?? null);
                // Cleared so picking the same file twice still fires a change.
                event.target.value = "";
              }}
            />
          </div>

          <label className="cedit__field">
            <span>Name</span>
            <input
              autoFocus
              value={name}
              maxLength={80}
              onChange={(event) => setName(event.target.value)}
            />
          </label>

          <fieldset className="cedit__colors">
            <legend>Client name color</legend>
            <div className="cedit__swatches">
              {NAME_COLORS.map((color) => (
                <button
                  key={color.value}
                  type="button"
                  className={nameColor.toLowerCase() === color.value ? "cedit__swatch cedit__swatch--on" : "cedit__swatch"}
                  style={{ backgroundColor: color.value }}
                  aria-label={color.label}
                  aria-pressed={nameColor.toLowerCase() === color.value}
                  onClick={() => setNameColor(color.value)}
                />
              ))}
              <label className="cedit__customcolor">
                <input
                  type="color"
                  value={nameColor}
                  onChange={(event) => setNameColor(event.target.value)}
                />
                Custom
              </label>
            </div>
            <span className="cedit__namepreview" style={{ color: nameColor }}>
              {name.trim() || "Client name"}
            </span>
          </fieldset>

          <label className="cedit__field">
            <span>Status</span>
            <input
              value={status}
              maxLength={40}
              list="client-statuses"
              placeholder="Active"
              onChange={(event) => setStatus(event.target.value)}
            />
            {/* Suggestions, not a list of permitted values — anything short is fine. */}
            <datalist id="client-statuses">
              {SUGGESTED_STATUSES.map((option) => (
                <option key={option} value={option} />
              ))}
            </datalist>
          </label>

          <label className="cedit__field">
            <span>Slack channel</span>
            <input
              value={channel}
              maxLength={32}
              placeholder="C0123456789"
              onChange={(event) => setChannel(event.target.value)}
            />
          </label>

          <fieldset className="cedit__privacy">
            <legend>Who can see this board</legend>
            <label className="cedit__toggle">
              <input
                type="checkbox"
                checked={isPrivate}
                onChange={(event) => setIsPrivate(event.target.checked)}
              />
              <span>
                <b>Private</b>
                <small>
                  {isPrivate
                    ? "Only the people ticked below, plus every admin."
                    : "Everyone signed in can see this board."}
                </small>
              </span>
            </label>

            {isPrivate ? (
              <>
                {accessState === "loading" ? <p className="cedit__hint">Loading the list…</p> : null}
                {accessState === "error" ? (
                  <p className="cedit__problem">The list couldn’t be loaded.</p>
                ) : null}
                {accessState === "ready" ? (
                  <ul className="cedit__people">
                    {users.map((person) => (
                      <li key={person.id}>
                        <label>
                          <input
                            type="checkbox"
                            // An admin already sees every board, so ticking one
                            // would be a promise the list is not keeping.
                            disabled={person.role === "admin"}
                            checked={person.role === "admin" || allowed.has(person.id)}
                            onChange={(event) => {
                              setAllowed((current) => {
                                const next = new Set(current);
                                if (event.target.checked) next.add(person.id);
                                else next.delete(person.id);
                                return next;
                              });
                            }}
                          />
                          <span>{person.name}</span>
                          {person.role === "admin" ? <em>admin — always</em> : null}
                        </label>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </>
            ) : null}
          </fieldset>

          <p className="cedit__hint">
            The slug this board is reachable at — <code>/c/{client.slug}</code> — is fixed when the
            client is created, so old links keep working after a rename.
          </p>
        </div>

        <footer className="cedit__foot">
          <div className="cedit__footleft">
            <button type="button" className="cedit__trash" disabled={busy}
              onClick={() => void moveToRecycleBin()}>Move to recycle bin</button>
            {problem ? <span role="alert" className="cedit__problem">{problem}</span> : null}
          </div>
          <div>
            <button type="button" onClick={onDone}>
              Cancel
            </button>
            <button type="submit" className="cedit__save" disabled={busy || !name.trim()}>
              {busy ? "Saving…" : "Save"}
            </button>
          </div>
        </footer>
      </form>
    </div>
  );
}
